import React, { useState, useEffect } from "react";
import { TranslationObject } from "./types";

interface JsonViewerProps {
  data: TranslationObject;
  onSave: (data: TranslationObject) => Promise<any>;
}

// A component for displaying and editing JSON with buttons for copy, paste, and save
const JsonViewer: React.FC<JsonViewerProps> = ({ data, onSave }) => {
  const [jsonText, setJsonText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Update jsonText when data changes
  useEffect(() => {
    setJsonText(JSON.stringify(data, null, 2));
    setError(null);
  }, [data]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonText(e.target.value);
    setError(null);
  };

  const handleSave = async () => {
    // Validate JSON before saving
    try {
      const parsedJson = JSON.parse(jsonText) as TranslationObject;
      setIsSaving(true);

      try {
        if (onSave) {
          await onSave(parsedJson);
        }
        setError(null);
      } catch (err) {
        setError(
          `Failed to save: ${err instanceof Error ? err.message : String(err)}`
        );
      } finally {
        setIsSaving(false);
      }
    } catch (err) {
      setError(
        `Invalid JSON: ${err instanceof Error ? err.message : String(err)}`
      );
    }
  };

  const handleCopy = () => {
    navigator.clipboard
      .writeText(jsonText)
      .then(() => {
        // Create a short-lived success notification
        const notification = document.createElement("div");
        notification.textContent = "Copied to clipboard!";
        notification.className =
          "fixed bottom-4 right-4 bg-cyan-600 text-white px-4 py-2 rounded-md shadow-lg";
        document.body.appendChild(notification);

        // Remove notification after 2 seconds
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 2000);
      })
      .catch((err) => {
        setError(
          `Failed to copy: ${err instanceof Error ? err.message : String(err)}`
        );
      });
  };

  const handlePaste = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();

      // Validate the pasted content is valid JSON
      JSON.parse(clipboardText);

      setJsonText(clipboardText);
      setError(null);
    } catch (err) {
      setError(
        `Invalid JSON from clipboard: ${
          err instanceof Error ? err.message : String(err)
        }`
      );
    }
  };

  const prettyPrint = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setJsonText(JSON.stringify(parsed, null, 2));
      setError(null);
    } catch (err) {
      setError(`Cannot format: Invalid JSON`);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="border-b border-gray-200 bg-gray-50 p-4 flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">JSON View</h3>
        <div className="flex space-x-2">
          <button
            onClick={handleCopy}
            className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
            title="Copy JSON to clipboard"
          >
            Copy
          </button>
          <button
            onClick={handlePaste}
            className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
            title="Paste JSON from clipboard"
          >
            Paste
          </button>
          <button
            onClick={prettyPrint}
            className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
            title="Format JSON"
          >
            Format
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !!error}
            className={`px-3 py-1 rounded text-sm ${
              isSaving || error
                ? "bg-cyan-400 text-white cursor-not-allowed"
                : "bg-cyan-600 text-white hover:bg-cyan-700"
            }`}
            title="Save changes"
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm leading-5 text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="p-4">
        <textarea
          value={jsonText}
          onChange={handleChange}
          className="p-4 bg-gray-800 text-gray-200 font-mono w-full h-[600px] rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-500"
          spellCheck={false}
        />
      </div>
    </div>
  );
};

export default JsonViewer;
